import { useCallback } from "react";

import useRequest from "./useRequest";
import { API_EQUIPMENT, API_EQUIPMENT_BATCH_ASSIGN_SITE, API_EQUIPMENT_BY_OBJECT, BASE_ADMIN } from "../constants/api";

export type EquipmentStatus = "active" | "inactive";

export type EquipmentPayload = {
  objectId: string;
  siteId: number | null;
  building: string;
  designation: string;
  elementType: string;
  location: string;
  repere: string;
  SapPosteTechnique: string;
  status: EquipmentStatus;
  ealicoLink: string;
  ealicoLinkTitle: string;
  ealicoLink2: string;
  ealicoLink2Title: string;
  otherLink1Title: string;
  otherLink1Url: string;
  otherLink2Title: string;
  otherLink2Url: string;
  SapEquipement: string;
};

export type EquipmentData = EquipmentPayload & {
  id: number;
  connectedObjectId: number | null;
  site?: string | null;
  createdAt: string;
  updatedAt: string;
  ConnectedObject?: { url: string } | null;
};

type EquipmentResponse = {
  message?: string;
  data?: EquipmentData;
};

const useEquipment = () => {
  const { error, isLoading, response, request } = useRequest<EquipmentResponse>();

  const fetchEquipment = useCallback((objectUrl: string) => {
    request(BASE_ADMIN, `${API_EQUIPMENT}/${API_EQUIPMENT_BY_OBJECT}/${encodeURIComponent(objectUrl)}`, { secure: true });
  }, [request]);

  const createEquipment = useCallback((payload: EquipmentPayload) => {
    request(BASE_ADMIN, API_EQUIPMENT, { method: "POST", secure: true }, payload);
  }, [request]);

  const updateEquipment = useCallback((equipmentId: number, payload: EquipmentPayload) => {
    request(BASE_ADMIN, `${API_EQUIPMENT}/${equipmentId}`, { method: "PUT", secure: true }, payload);
  }, [request]);

  const removeEquipment = useCallback((equipmentId: number) => {
    request(BASE_ADMIN, `${API_EQUIPMENT}/${equipmentId}`, { method: "DELETE", secure: true });
  }, [request]);

  return {
    error,
    isLoading,
    response,
    fetchEquipment,
    createEquipment,
    updateEquipment,
    removeEquipment
  };
};

export type BatchAssignSkipped = {
  objectId: string;
  reason: string;
};

export type BatchAssignSiteResponse = {
  created: number;
  skipped: BatchAssignSkipped[];
};

export const useBatchAssignSite = () => {
  const { error, isLoading, response, request } = useRequest<BatchAssignSiteResponse>();

  const assignSite = useCallback((siteId: number, objectIds: string[]) => {
    request(
      BASE_ADMIN,
      `${API_EQUIPMENT}/${API_EQUIPMENT_BATCH_ASSIGN_SITE}`,
      { method: "POST", secure: true },
      { siteId, objectIds }
    );
  }, [request]);

  return { error, isLoading, response, assignSite };
};

export default useEquipment;
